import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClientsStatus } from '@kkp-fraud/shared';
import { ApiError, COOKIE_MODE, apiClientsApi } from '../api';
import { ActionMenu } from '../components/ActionMenu';
import { useShell } from '../components/AppLayout';
import { Dialog } from '../components/Dialog';
import { Field, FieldGroup, useFieldErrors } from '../components/Field';
import { PageHead } from '../components/PageHead';
import { When } from '../components/When';
import { isDemoApiClientId } from '../demo/apiClients';
import { DEMO } from '../demo';
import { useRuleSets } from '../hooks/useRuleSets';
import { hasKey, t } from '../i18n';
import { liveSets, type RuleSetList } from '../ruleSets';
import { canManageUsers } from '../session';
import {
  PROBLEM_FIELD,
  SERVER_PROBLEM_CODES,
  apiClientErrorKey,
  apiClientFormProblems,
  bindingOf,
  curlExample,
  expiryDate,
  expiryInstant,
  maskedKey,
  toggleSet,
  type ApiClientForm,
  type ApiClientProblem,
  type ApiClientStatus,
  type ApiClientView,
  type ApiClientWithKey,
  visibleClients,
} from '../apiClients';
import { ApiReferencePanel } from './ApiReferencePanel';

/**
 * API Keys (Wave 4) — one key per caller of the decision API's `/v1/*`. Admin only: the navigation
 * entry is hidden from everyone else, and a deep link shows a refusal (the admin service answers
 * 403 to them anyway).
 *
 * Create (the key is shown ONCE, with a ready-made curl example), edit the name, description and
 * expiry, switch a client off and on, rotate its key (the new one is shown once; the old one stops
 * at the engine's next poll), and revoke it. There is no delete and no rename: a client's id is in
 * every decision it authorised, so a revoked client stays listed. Only the key's first characters
 * are ever shown again (`kkpf_abcd1234…`) — the admin service keeps nothing else.
 *
 * Wave 5: a key may be bound to rule sets (create dialog, and the row menu's "Edit rule sets"):
 * only those judge its traffic. Unbound = every enabled set, as before; every bound set off = the
 * primary judges (a warning chip says so).
 */
export function ApiKeysScreen() {
  const { settings, session, showToast } = useShell();
  const allowed = COOKIE_MODE && canManageUsers(session);
  const [clients, setClients] = useState<ApiClientView[] | null>(null);
  const [engine, setEngine] = useState<ApiClientsStatus | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ApiClientView | null>(null);
  const [rotating, setRotating] = useState<ApiClientView | null>(null);
  const [revoking, setRevoking] = useState<ApiClientView | null>(null);
  const [binding, setBinding] = useState<ApiClientView | null>(null);
  // The key list and the API reference (code examples, Try it) — two views of one screen.
  const [view, setView] = useState<'keys' | 'reference'>('keys');
  // Revoked keys are final and pile up (acceptance revokes its own every run): hidden by default.
  const [showRevoked, setShowRevoked] = useState(false);
  // Wave 5 — the registry, for the Rule sets column and the pickers (null = not known).
  const { list: ruleSets } = useRuleSets(settings);

  const reload = useCallback(async () => {
    try {
      const got = await apiClientsApi.list(settings);
      setClients(Array.isArray(got) ? got : []);
      setError(null);
    } catch (e) {
      setClients([]);
      setError(errorText(e));
    }
  }, [settings]);

  useEffect(() => {
    if (!allowed) return;
    void reload();
    void apiClientsApi.engineStatus(settings).then(setEngine, () => setEngine(null));
  }, [allowed, reload, settings]);

  if (!allowed) {
    return (
      <>
        <PageHead title={t('apikeys.title')} lead={t('apikeys.lead')} />
        <div className="banner warn" role="status" data-testid="apikeys-forbidden">
          {t('apikeys.forbidden')}
        </div>
      </>
    );
  }

  const writable = (c: ApiClientView) => c.status !== 'revoked' && !(DEMO && isDemoApiClientId(c.id));
  const shown = clients ? visibleClients(clients, showRevoked) : null;
  const revokedCount = clients?.filter((c) => c.status === 'revoked').length ?? 0;

  const setEnabled = async (c: ApiClientView, enabled: boolean) => {
    try {
      await apiClientsApi.update(settings, c.id, { enabled }, c.etag);
      showToast(t(enabled ? 'apikeys.enabled.toast' : 'apikeys.disabled.toast', { name: c.name }));
    } catch (e) {
      setError(errorText(e));
    }
    void reload();
  };

  return (
    <>
      <PageHead title={t('apikeys.title')} lead={t('apikeys.lead')} />

      <nav className="tabs" role="tablist" aria-label={t('apikeys.tabs.a11y')}>
        {(['keys', 'reference'] as const).map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            className="tab"
            aria-selected={view === id}
            data-testid={`apikeys-tab-${id}`}
            onClick={() => setView(id)}
          >
            {t(`apikeys.tab.${id}`)}
          </button>
        ))}
      </nav>

      {view === 'reference' && <ApiReferencePanel />}

      {view === 'keys' && error && (
        <div className="banner error" role="status" data-testid="apikeys-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}

      {view === 'keys' && (
      <section className="card">
        <header className="card-head">
          <h2>{t('apikeys.list.title')}</h2>
          {shown && (
            <span className="chip mute" data-testid="apikeys-count">
              {t('apikeys.list.count', { n: shown.length })}
            </span>
          )}
          <span className="spacer" />
          {revokedCount > 0 && (
            <label className="check small" data-testid="apikeys-show-revoked">
              <input type="checkbox" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} />
              {t('apikeys.revoked.toggle', { n: revokedCount })}
            </label>
          )}
          <button type="button" className="btn small" data-testid="apikeys-new" onClick={() => setCreating(true)}>
            {t('apikeys.new.action')}
          </button>
        </header>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('apikeys.col.name')}</th>
                <th style={{ width: 170 }}>{t('apikeys.col.key')}</th>
                <th style={{ width: 110 }}>{t('apikeys.col.status')}</th>
                <th style={{ width: 200 }}>{t('apikeys.col.rule_sets')}</th>
                <th style={{ width: 170 }}>{t('apikeys.col.created')}</th>
                <th style={{ width: 170 }}>{t('apikeys.col.expires')}</th>
                <th style={{ width: 44 }} aria-label={t('apikeys.col.actions')} />
              </tr>
            </thead>
            <tbody>
              {clients === null && (
                <tr>
                  <td colSpan={7} className="empty">
                    {t('apikeys.loading')}
                  </td>
                </tr>
              )}
              {clients?.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty" role="status" data-testid="apikeys-empty">
                    {t('apikeys.empty')}
                  </td>
                </tr>
              )}
              {clients && clients.length > 0 && shown?.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty" role="status" data-testid="apikeys-all-revoked">
                    {t('apikeys.revoked.all_hidden')}
                  </td>
                </tr>
              )}
              {shown?.map((c) => {
                const canWrite = writable(c);
                const demo = DEMO && isDemoApiClientId(c.id);
                return (
                  <tr
                    key={c.id}
                    data-testid={`apikey-row-${c.id}`}
                    // A row opens its client's edit dialog; the name is the keyboard way in (UX review P2-10).
                    className={canWrite ? 'clickable' : undefined}
                    onClick={canWrite ? () => setEditing(c) : undefined}
                  >
                    <td>
                      {canWrite ? (
                        <button
                          type="button"
                          className="rowlink"
                          data-testid={`apikey-open-${c.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditing(c);
                          }}
                        >
                          {c.name}
                        </button>
                      ) : (
                        c.name
                      )}
                      {demo && (
                        <span className="chip warn" style={{ marginLeft: 6 }}>
                          {t('master.demo.chip')}
                        </span>
                      )}
                      <div className="small muted mono">{c.id}</div>
                      {c.description && <div className="small muted">{c.description}</div>}
                    </td>
                    <td className="mono small" data-testid={`apikey-prefix-${c.id}`}>
                      {maskedKey(c.keyPrefix)}
                    </td>
                    <td>
                      <StatusChip status={c.status} testid={`apikey-status-${c.id}`} />
                    </td>
                    <td data-testid={`apikey-sets-${c.id}`}>
                      <BindingChips ruleSets={c.ruleSets} list={ruleSets} testid={`apikey-sets-fallback-${c.id}`} />
                    </td>
                    <td className="small">
                      <When iso={c.createdUtc} />
                    </td>
                    <td className="small">{c.expiresUtc ? <When iso={c.expiresUtc} /> : <span className="muted">{t('apikeys.expires.never')}</span>}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {/* Everyday actions first; revoke — final — last, set apart and confirmed (UX review P2-10). */}
                      {canWrite && (
                        <ActionMenu
                          variant="row"
                          label={t('apikeys.menu.a11y', { name: c.name })}
                          items={[
                            { label: t('apikeys.menu.edit'), onSelect: () => setEditing(c) },
                            { label: t('apikeys.menu.rule_sets'), onSelect: () => setBinding(c) },
                            { label: t('apikeys.menu.rotate'), onSelect: () => setRotating(c) },
                            c.enabled
                              ? { label: t('apikeys.menu.disable'), onSelect: () => void setEnabled(c, false) }
                              : { label: t('apikeys.menu.enable'), onSelect: () => void setEnabled(c, true) },
                            { label: t('apikeys.menu.revoke'), onSelect: () => setRevoking(c), separated: true },
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="small muted" data-testid="apikeys-engine" style={{ padding: '8px 12px' }}>
          {engineText(engine)}
        </div>
      </section>
      )}

      {creating && (
        <ClientDialog
          ruleSets={ruleSets}
          onClose={() => {
            setCreating(false);
            void reload();
          }}
          onSaved={() => undefined}
        />
      )}
      {editing && (
        <ClientDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={(c) => {
            setEditing(null);
            showToast(t('apikeys.saved.toast', { name: c.name }));
            void reload();
          }}
        />
      )}
      {binding && (
        <RuleSetsDialog
          client={binding}
          list={ruleSets}
          onClose={() => setBinding(null)}
          onSaved={(c) => {
            setBinding(null);
            showToast(t('apikeys.rulesets.saved.toast', { name: c.name }));
            void reload();
          }}
        />
      )}
      {rotating && (
        <RotateDialog
          client={rotating}
          onClose={() => {
            setRotating(null);
            void reload();
          }}
        />
      )}
      {revoking && (
        <RevokeDialog
          client={revoking}
          onClose={() => setRevoking(null)}
          onRevoked={() => {
            showToast(t('apikeys.revoked.toast', { name: revoking.name }));
            setRevoking(null);
            void reload();
          }}
        />
      )}
    </>
  );
}

function engineText(engine: ApiClientsStatus | null | undefined): string {
  if (engine === undefined) return '';
  if (engine === null) return t('apikeys.engine.unknown');
  if (!engine.loaded) return t('apikeys.engine.not_loaded', { source: engine.source });
  return t('apikeys.engine.loaded', { n: engine.count, source: engine.source });
}

/** The admin service's refusal in words: a known code, else its coded problems, else its message. */
function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const key = apiClientErrorKey(e.code);
    if (key) return t(key);
    const coded = (e.problems ?? []).filter((p) => p.code && SERVER_PROBLEM_CODES.includes(p.code) && hasKey(`apikeys.problem.${p.code}`));
    if (coded.length > 0) return coded.map((p) => t(`apikeys.problem.${p.code}`)).join(' ');
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

const STATUS_CLASS: Record<ApiClientStatus, string> = {
  active: 'chip ok',
  disabled: 'chip mute',
  expired: 'chip warn',
  revoked: 'chip bad',
};

/**
 * Wave 5 — a key's binding: one chip per bound set (muted while it is off), "All enabled sets"
 * when unbound, and a warning chip when every bound set is off — the primary judges instead.
 */
function BindingChips({ ruleSets, list, testid }: { ruleSets: string[] | undefined; list: RuleSetList | null; testid: string }) {
  const b = bindingOf(ruleSets, list);
  if (b.unbound) return <span className="chip mute">{t('apikeys.rulesets.all')}</span>;
  return (
    <div className="row-wrap" style={{ gap: 4 }}>
      {b.sets.map((s) => (
        <span
          key={s.id}
          className={s.live || !list ? 'chip info' : 'chip mute'}
          title={s.known ? s.id : t('apikeys.rulesets.unknown_set', { id: s.id })}
        >
          {s.name}
          {list && !s.live && ` · ${t(s.known ? 'apikeys.rulesets.off' : 'apikeys.rulesets.missing')}`}
        </span>
      ))}
      {b.fallsBack && (
        <span className="chip warn" data-testid={testid} title={t('apikeys.rulesets.fallback.tip')}>
          {t('apikeys.rulesets.fallback')}
        </span>
      )}
    </div>
  );
}

/**
 * Wave 5 — tick the rule sets that judge a key's traffic: every set that is not archived, the
 * primary first. A bound set that is archived or gone since stays listed (ticked) so it can be
 * removed. Nothing ticked = every enabled set.
 */
function RuleSetPicker({ value, list, onChange }: { value: string[]; list: RuleSetList | null; onChange: (next: string[]) => void }) {
  if (!list) {
    return (
      <div className="small muted" data-testid="apikey-rulesets-unknown">
        {t('apikeys.form.rule_sets.unknown')}
      </div>
    );
  }
  const offered = liveSets(list.sets);
  const stale = value.filter((id) => !offered.some((s) => s.id === id));
  return (
    <FieldGroup legend={t('apikeys.form.rule_sets')} hint={t('apikeys.form.rule_sets.tip')} testid="apikey-rulesets">
      {offered.map((s) => (
        <label key={s.id} className="check" title={s.id}>
          <input
            type="checkbox"
            data-testid={`apikey-ruleset-${s.id}`}
            checked={value.includes(s.id)}
            onChange={(e) => onChange(toggleSet(value, s.id, e.target.checked))}
          />
          <span>
            {s.name || s.id} <span className="mono small muted">{s.id}</span>
            {s.primary && <span className="chip mute" style={{ marginLeft: 6 }}>{t('apikeys.rulesets.primary')}</span>}
            {!s.enabled && <span className="chip mute" style={{ marginLeft: 6 }}>{t('apikeys.rulesets.off')}</span>}
          </span>
        </label>
      ))}
      {stale.map((id) => (
        <label key={id} className="check">
          <input type="checkbox" data-testid={`apikey-ruleset-${id}`} checked onChange={() => onChange(toggleSet(value, id, false))} />
          <span className="mono small">{t('apikeys.rulesets.unknown_set', { id })}</span>
        </label>
      ))}
    </FieldGroup>
  );
}

/** A binding in which no set is on: the engine judges with the primary instead. */
function fallsBack(value: string[], list: RuleSetList | null): boolean {
  const b = bindingOf(value, list);
  return !b.unbound && b.fallsBack;
}

function RuleSetsDialog({ client, list, onClose, onSaved }: {
  client: ApiClientView;
  list: RuleSetList | null;
  onClose: () => void;
  onSaved: (c: ApiClientView) => void;
}) {
  const { settings } = useShell();
  const [value, setValue] = useState<string[]>(client.ruleSets ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await apiClientsApi.update(settings, client.id, { ruleSets: value }, client.etag));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      busy={busy}
      title={t('apikeys.rulesets.title', { name: client.name })}
      testid="apikey-rulesets-dialog"
      onClose={onClose}
      foot={<Foot busy={busy || !list} label={t('apikeys.save.action')} testid="apikey-rulesets-save" onClose={onClose} onGo={() => void go()} />}
    >
      <RuleSetPicker value={value} list={list} onChange={setValue} />
      {fallsBack(value, list) && (
        <div className="banner warn" role="status" data-testid="apikey-rulesets-fallback">
          {t('apikeys.rulesets.fallback.tip')}
        </div>
      )}
      <Problem testid="apikey-rulesets-error" text={error} />
    </Dialog>
  );
}

function StatusChip({ status, testid }: { status: ApiClientStatus; testid: string }) {
  return (
    <span className={STATUS_CLASS[status]} data-testid={testid}>
      {t(`apikeys.status.${status}`)}
    </span>
  );
}

// ---- dialogs ---------------------------------------------------------------------------

/** A problem that belongs to no single field — the admin service's refusal. */
function Problem({ text, testid }: { text: string | null; testid: string }) {
  if (!text) return null;
  return (
    <div className="why-missing" role="alert" data-testid={testid} style={{ margin: 0 }}>
      {text}
    </div>
  );
}

function errorOf(problems: readonly ApiClientProblem[], field: keyof ApiClientForm): string | undefined {
  const p = problems.find((x) => PROBLEM_FIELD[x] === field);
  return p ? t(`apikeys.problem.${p}`) : undefined;
}

const dialogRoot = (testid: string) => document.querySelector(`[data-testid="${testid}"]`);

function Foot({ busy, label, testid, onClose, onGo, danger }: {
  busy: boolean;
  label: string;
  testid: string;
  onClose: () => void;
  onGo: () => void;
  danger?: boolean;
}) {
  return (
    <>
      <span className="spacer" />
      <button type="button" className="btn" onClick={onClose}>
        {t('btn.cancel.label')}
      </button>
      <button type="button" className={danger ? 'btn danger' : 'btn primary'} data-testid={testid} disabled={busy} onClick={onGo}>
        {label}
      </button>
    </>
  );
}

/**
 * New client (no `initial`) or edit one. A new client's key is shown in this same dialog once it
 * is created — the dialog then only closes; the key is gone with it.
 */
function ClientDialog({ initial, ruleSets, onClose, onSaved }: {
  initial?: ApiClientView;
  /** The registry for the create dialog's rule-set picker (Wave 5); the edit dialog has its own. */
  ruleSets?: RuleSetList | null;
  onClose: () => void;
  onSaved: (c: ApiClientView) => void;
}) {
  const { settings } = useShell();
  const isNew = initial === undefined;
  const [values, setValues] = useState<ApiClientForm>({
    id: initial?.id ?? '',
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    expiresDate: expiryDate(initial?.expiresUtc),
  });
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [bound, setBound] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [issued, setIssued] = useState<ApiClientWithKey | null>(null);
  const form = useFieldErrors();

  // An unchanged expiry is never re-checked: a client may already be past it (status "expired").
  const expiryChanged = values.expiresDate !== expiryDate(initial?.expiresUtc);
  const problems = apiClientFormProblems(expiryChanged ? values : { ...values, expiresDate: '' }, isNew);
  const errorFor = (field: keyof ApiClientForm) => form.show(field, errorOf(problems, field));
  const patch = (p: Partial<ApiClientForm>) => setValues((f) => ({ ...f, ...p }));

  if (issued) return <OneTimeKey client={issued} title={t('apikeys.key.title', { name: issued.name })} onClose={onClose} />;

  const save = async () => {
    if (!form.attempt(problems.length === 0, dialogRoot('apikey-dialog'))) return;
    setBusy(true);
    setServerError(null);
    try {
      const description = values.description.trim();
      const expires = values.expiresDate === '' ? null : expiryInstant(values.expiresDate);
      if (isNew) {
        const created = await apiClientsApi.create(settings, {
          id: values.id.trim(),
          name: values.name.trim(),
          ...(description ? { description } : {}),
          ...(expires ? { expiresUtc: expires } : {}),
          ...(bound.length > 0 ? { ruleSets: bound } : {}),
        });
        setIssued(created);
        onSaved(created);
      } else {
        const saved = await apiClientsApi.update(
          settings,
          initial.id,
          {
            name: values.name.trim(),
            description: description || null,
            enabled,
            ...(expiryChanged ? { expiresUtc: expires } : {}),
          },
          initial.etag,
        );
        onSaved(saved);
      }
    } catch (e) {
      setServerError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      busy={busy}
      title={isNew ? t('apikeys.create.title') : t('apikeys.edit.title', { name: initial.name })}
      testid="apikey-dialog"
      onClose={onClose}
      foot={
        <Foot
          busy={busy}
          label={isNew ? t('apikeys.create.action') : t('apikeys.save.action')}
          testid="apikey-save"
          onClose={onClose}
          onGo={() => void save()}
        />
      }
    >
      <Field
        label={t('apikeys.form.id')}
        required={isNew}
        hint={isNew ? t('apikeys.form.id.tip') : undefined}
        error={isNew ? errorFor('id') : undefined}
        name="id"
        form={form}
        testid="apikey-field-id"
      >
        <input
          type="text"
          className="mono-input"
          value={values.id}
          disabled={!isNew}
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => patch({ id: e.target.value.toLowerCase() })}
        />
      </Field>
      <Field label={t('apikeys.form.name')} required error={errorFor('name')} name="name" form={form} testid="apikey-field-name">
        <input type="text" value={values.name} onChange={(e) => patch({ name: e.target.value })} />
      </Field>
      <Field label={t('apikeys.form.description')} error={errorFor('description')} name="description" form={form} testid="apikey-field-description">
        <input type="text" value={values.description} onChange={(e) => patch({ description: e.target.value })} />
      </Field>
      <Field
        label={t('apikeys.form.expires')}
        hint={t('apikeys.form.expires.tip')}
        error={errorFor('expiresDate')}
        name="expiresDate"
        form={form}
        testid="apikey-field-expires"
      >
        <input type="date" value={values.expiresDate} onChange={(e) => patch({ expiresDate: e.target.value })} />
      </Field>
      {isNew && (
        <>
          <RuleSetPicker value={bound} list={ruleSets ?? null} onChange={setBound} />
          {fallsBack(bound, ruleSets ?? null) && (
            <div className="banner warn" role="status" data-testid="apikey-rulesets-fallback">
              {t('apikeys.rulesets.fallback.tip')}
            </div>
          )}
        </>
      )}
      {!isNew && (
        <label className="check">
          <input type="checkbox" data-testid="apikey-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          {t('apikeys.form.enabled')}
        </label>
      )}
      <Problem testid="apikey-form-error" text={serverError} />
    </Dialog>
  );
}

function RotateDialog({ client, onClose }: { client: ApiClientView; onClose: () => void }) {
  const { settings } = useShell();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<ApiClientWithKey | null>(null);

  if (issued) return <OneTimeKey client={issued} title={t('apikeys.key.rotated.title', { name: issued.name })} onClose={onClose} />;

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      setIssued(await apiClientsApi.rotate(settings, client.id, client.etag));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      busy={busy}
      title={t('apikeys.rotate.title', { name: client.name })}
      testid="apikey-rotate-dialog"
      onClose={onClose}
      foot={<Foot busy={busy} label={t('apikeys.rotate.action')} testid="apikey-rotate-go" onClose={onClose} onGo={() => void go()} />}
    >
      <div className="small">{t('apikeys.rotate.lead', { key: maskedKey(client.keyPrefix) })}</div>
      <Problem testid="apikey-rotate-error" text={error} />
    </Dialog>
  );
}

function RevokeDialog({ client, onClose, onRevoked }: { client: ApiClientView; onClose: () => void; onRevoked: () => void }) {
  const { settings } = useShell();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiClientsApi.revoke(settings, client.id, client.etag);
      onRevoked();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      busy={busy}
      title={t('apikeys.revoke.title', { name: client.name })}
      testid="apikey-revoke-dialog"
      onClose={onClose}
      foot={<Foot busy={busy} danger label={t('apikeys.revoke.action')} testid="apikey-revoke-go" onClose={onClose} onGo={() => void go()} />}
    >
      <div className="small">{t('apikeys.revoke.lead', { name: client.name, key: maskedKey(client.keyPrefix) })}</div>
      <Problem testid="apikey-revoke-error" text={error} />
    </Dialog>
  );
}

/**
 * The key, once: a copy button, the ready-made curl example and the warning that it will not be
 * shown again. The key lives only in the state of the dialog that fetched it.
 */
function OneTimeKey({ client, title, onClose }: { client: ApiClientWithKey; title: string; onClose: () => void }) {
  // QA-A-15: the decision API this console belongs to, as the admin service names it.
  const curl = curlExample(client.key, client.apiBase);
  // QA-A-14: Escape, the scrim and ✕ ask first — this is the only copy of the key. While asking,
  // the frame holds (`busy`), so a second Escape cannot close it behind the question.
  const [confirming, setConfirming] = useState(false);
  const keep = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) keep.current?.focus();
  }, [confirming]);
  return (
    <Dialog
      wide
      title={title}
      testid="apikey-key-dialog"
      busy={confirming}
      onClose={() => setConfirming(true)}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn primary" data-testid="apikey-key-done" onClick={onClose}>
            {t('apikeys.key.done')}
          </button>
        </>
      }
    >
      {confirming && (
        <div className="banner warn" role="alertdialog" aria-label={t('apikeys.key.discard.question')} data-testid="apikey-key-discard-confirm">
          <div>{t('apikeys.key.discard.question')}</div>
          <div className="row-wrap" style={{ marginTop: 8 }}>
            <button ref={keep} type="button" className="btn small primary" data-testid="apikey-key-keep" onClick={() => setConfirming(false)}>
              {t('apikeys.key.discard.keep')}
            </button>
            <button type="button" className="btn small" data-testid="apikey-key-discard" onClick={onClose}>
              {t('apikeys.key.discard.close')}
            </button>
          </div>
        </div>
      )}
      <div className="banner warn" role="alert" data-testid="apikey-key-warning">
        {t('apikeys.key.warning')}
      </div>
      <div>
        <div className="row-wrap" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <b>{t('apikeys.key.label')}</b>
          <CopyButton text={client.key} label={t('apikeys.key.copy')} testid="apikey-key-copy" />
        </div>
        <pre className="wh-pre" data-testid="apikey-key-value">
          {client.key}
        </pre>
      </div>
      <div>
        <div className="row-wrap" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <b>{t('apikeys.curl.label')}</b>
          <CopyButton text={curl} label={t('apikeys.curl.copy')} testid="apikey-curl-copy" />
        </div>
        <div className="small muted" style={{ marginBottom: 4 }}>
          {t('apikeys.curl.lead')}
        </div>
        <pre className="wh-pre" data-testid="apikey-curl">
          {curl}
        </pre>
      </div>
    </Dialog>
  );
}

function CopyButton({ text, label, testid }: { text: string; label: string; testid: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <button
      type="button"
      className="btn small"
      data-testid={testid}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(
          () => setCopied(true),
          () => undefined,
        );
      }}
    >
      {copied ? t('apikeys.key.copied') : label}
    </button>
  );
}
